import { createBrowserRouter, Navigate } from "react-router";

import { AppLayout } from "./layouts/AppLayout";
import {
  RequireAuth,
  RequireCurrentConsents,
  RequireOnboarding,
  RequirePermission,
  RequireTenant,
  RequireVerifiedEmail,
} from "./lib/guards";
import { AccountSecurityPage } from "./pages/account/AccountSecurityPage";
import { ConsentsPage } from "./pages/account/ConsentsPage";
import { EmailChangeConfirmPage } from "./pages/account/EmailChangeConfirmPage";
import { PreferencesPage } from "./pages/account/PreferencesPage";
import { AuditPage } from "./pages/audit/AuditPage";
import { AuthErrorPage } from "./pages/auth/AuthErrorPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { LoginPage } from "./pages/auth/LoginPage";
import { ReconsentPage } from "./pages/auth/ReconsentPage";
import { RegisterPage } from "./pages/auth/RegisterPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/auth/VerifyEmailPage";
import { ClientDetailPage } from "./pages/clients/ClientDetailPage";
import { ClientsLayout } from "./pages/clients/ClientsLayout";
import { ClientsPage } from "./pages/clients/ClientsPage";
import { SponsoredWorkspacesPage } from "./pages/clients/SponsoredWorkspacesPage";
import { ClientWorkspacePage } from "./pages/clientWorkspace/ClientWorkspacePage";
import { ContractAiDraftPage } from "./pages/contracts/ContractAiDraftPage";
import { ContractComparePage } from "./pages/contracts/ContractComparePage";
import { ContractDetailLayout } from "./pages/contracts/ContractDetailLayout";
import { ContractDocumentHubPage } from "./pages/contracts/ContractDocumentHubPage";
import { ContractDocumentPage } from "./pages/contracts/ContractDocumentPage";
import { ContractDocxEditor } from "./pages/contracts/ContractDocxEditor";
import { ContractOverviewPage } from "./pages/contracts/ContractOverviewPage";
import { ContractPartiesAccessPage } from "./pages/contracts/ContractPartiesAccessPage";
import { ContractProcessPage } from "./pages/contracts/ContractProcessPage";
import { ContractRecordPage } from "./pages/contracts/ContractRecordPage";
import { ContractRevisionsPage } from "./pages/contracts/ContractRevisionsPage";
import { ContractsLayout } from "./pages/contracts/ContractsLayout";
import { ContractsPage } from "./pages/contracts/ContractsPage";
import {
  ContractVersionDetailPage,
  ContractVersionsPage,
} from "./pages/contracts/ContractVersionsPage";
import { MetinYonlendirmesi } from "./pages/contracts/MetinYonlendirmesi";
import { SharedContractDetailPage } from "./pages/contracts/SharedContractDetailPage";
import { SharedContractsPage } from "./pages/contracts/SharedContractsPage";
import { TemplateContractPage } from "./pages/contracts/TemplateContractPage";
import { ExternalContractDetailPage } from "./pages/external/ExternalContractDetailPage";
import { ExternalContractsPage } from "./pages/external/ExternalContractsPage";
import { ExternalShell } from "./pages/external/ExternalShell";
import { HomePage } from "./pages/home/HomePage";
import { ContractOrgInvitationPage } from "./pages/invitations/ContractOrgInvitationPage";
import { InvitationPage } from "./pages/invitations/InvitationPage";
import { MyInvitationsPage } from "./pages/invitations/MyInvitationsPage";
import { ParticipantInvitationPage } from "./pages/invitations/ParticipantInvitationPage";
import { RepresentativeInvitationPage } from "./pages/invitations/RepresentativeInvitationPage";
import { NotificationsPage } from "./pages/notifications/NotificationsPage";
import { OnboardingPage } from "./pages/onboarding/OnboardingPage";
import { AlertPolicyPage } from "./pages/organization/AlertPolicyPage";
import { DepartmentsPage } from "./pages/organization/DepartmentsPage";
import { MembersPage } from "./pages/organization/MembersPage";
import { OrganizationLayout } from "./pages/organization/OrganizationLayout";
import { OrganizationProfilePage } from "./pages/organization/OrganizationProfilePage";
import { ParticipantRolesPage } from "./pages/organization/ParticipantRolesPage";
import { RolesPage } from "./pages/organization/RolesPage";
import { SecurityPolicyPage } from "./pages/organization/SecurityPolicyPage";
import { WorkflowBuilderPage } from "./pages/organization/WorkflowBuilderPage";
import { WorkflowsPage } from "./pages/organization/WorkflowsPage";
import { PendingTasksPage } from "./pages/pendingTasks/PendingTasksPage";
import { AddOnsPage } from "./pages/subscription/AddOnsPage";
import { BillingPage } from "./pages/subscription/BillingPage";
import { SubscriptionLayout } from "./pages/subscription/SubscriptionLayout";
import { SubscriptionOverviewPage } from "./pages/subscription/SubscriptionOverviewPage";
import { TemplateCreatePage } from "./pages/templates/TemplateCreatePage";
import { TemplateDetailPage } from "./pages/templates/TemplateDetailPage";
import { TemplateEditPage } from "./pages/templates/TemplateEditPage";
import { TemplatesPage } from "./pages/templates/TemplatesPage";
import { TenantsPage } from "./pages/tenants/TenantsPage";

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  /**
   * Sosyal giriş turu hata verdiğinde BetterAuth'un ham İngilizce sayfası yerine BURAYA
   * düşülüyor (`onAPIError.errorURL`). Oturum gerektirmez: hata zaten giriş yapılamadığı
   * için oluşuyor.
   */
  { path: "/giris-hatasi", element: <AuthErrorPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  // Doğrulama ekranı oturum GEREKTİRMEZ: kullanıcı e-postadaki bağlantıya başka bir
  // tarayıcıdan da tıklayabilir.
  { path: "/verify-email", element: <VerifyEmailPage /> },
  // Davet ekranı da oturum GEREKTİRMEZ (CLOS-74 `[035]`): davet edilen kişinin henüz
  // hesabı olmayabilir ve hangi organizasyona çağrıldığını kaydolmadan önce görmeli.
  // `RequireAuth` arkasında olsaydı davetli /login'e atılır, token yolda kaybolurdu —
  // rota hiç yokken catch-all'ın yaptığının aynısı.
  { path: "/davet", element: <InvitationPage /> },
  // Haricî katılımcı daveti de oturum GEREKTİRMEZ (CLOS-147 `[128]`): aynı gerekçe,
  // aynı token-koruma deseni. RequireAuth arkasında olsaydı token yolda kaybolurdu.
  { path: "/katilimci-davet", element: <ParticipantInvitationPage /> },
  // Temsilci daveti de oturum GEREKTİRMEZ (CLOS-177 `[124]`): aynı token-koruma deseni.
  { path: "/temsilci-davet", element: <RepresentativeInvitationPage /> },
  /**
   * Sözleşme tarafı daveti (CLOS-1132) — dördüncü token'lı sayfa; aynı gerekçeyle
   * `RequireAuth` DIŞINDA: davetli henüz hesap açmamış olabilir. Sayfa daveti GÖSTERİR,
   * karar Davetlerim'de.
   */
  { path: "/sozlesme-davet", element: <ContractOrgInvitationPage /> },
  /**
   * E-posta değişikliği onayı da oturum GEREKTİRMEZ (CLOS-225 `[048]`): kullanıcı
   * bağlantıya yeni adresinin posta kutusundan, çoğu zaman başka bir tarayıcıdan tıklıyor.
   * `RequireAuth` arkasında olsaydı `/login`'e atılır ve token yolda kaybolurdu.
   */
  { path: "/e-posta-degistir", element: <EmailChangeConfirmPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <RequireVerifiedEmail />,
        children: [
          // Yeniden onay ekranı onay KAPISININ DIŞINDA: içeride olsaydı kullanıcı kendi
          // çıkışına ulaşamaz, sonsuz yönlendirmeye girerdi.
          { path: "/consents", element: <ReconsentPage /> },
          {
            element: <RequireCurrentConsents />,
            children: [
              // Onboarding ekranı KAPININ DIŞINDA: içeride olsaydı sonsuz yönlendirme
              // olurdu (guard buraya atıyor, buradaki guard yine buraya atardı).
              { path: "/onboarding", element: <OnboardingPage /> },

              /**
               * Hesap düzeyi KABUK (kabuk kusuru, canlı turda görüldü): dört sayfa —
               * Onay geçmişi, Hesap ve Güvenlik, Tercihler, Davetlerim — HESAP düzeyinde,
               * çalışma alanına bağlı değil: `RequireOnboarding`/`RequireTenant`
               * arkasında dursaydı, henüz alan seçmemiş bir kullanıcı kendi hukuki
               * kayıtlarına, giriş adresine, temasına ya da davetine ULAŞAMAZDI.
               *
               * ⚠ Tenant'sız olmak KABUKSUZ olmak DEMEK DEĞİL: kullanıcı `/app`'ten Profil
               * menüsüyle buraya geliyor ve sidebar'ı, üst barı, profil menüsünü
               * kaybediyordu (elle turda görüldü: `/tercihler` menüsüz, dar bir sütunda
               * ortada açılıyordu).
               *
               * `AppLayout` TENANT'SIZ halde de çizilebiliyor: `Sidebar` `activeTenant?.type`
               * olmadığında boş listeye düşüyor (`visibleNavItems`), `WorkspaceSwitcher`
               * "Çalışma alanı seçilmedi" gösteriyor, Üst Bar'ın tenant'lı yuvaları
               * (`PendingTasksPanel`, `NotificationsPanel`, `NewContractAction`, `SearchOpener`)
               * kendi sorgularını `activeTenantId !== null` ile zaten kapatıyor — hiçbiri
               * tenant varsayımıyla çökmüyor. Bu yüzden AppLayout'un İKİNCİ bir örneği burada:
               * aynı bileşen, `/app`'ten AYRI bir dal altında, guard'sız (RequireOnboarding/
               * RequireTenant'ın hiçbiri bu dalda yok — hesap düzeyi sayfalar üyelik varsaymaz).
               *
               * ⚠ `/harici` (ExternalShell) BİLEREK bu dalın DIŞINDA kalıyor: o kararın gerekçesi
               * kendi yorumunda duruyor ("iç sidebar ve çalışma alanı menüsü buraya sızamaz") ve
               * burada DEĞİŞMİYOR — haricî katılımcı hâlâ AppLayout görmemeli.
               */
              {
                element: <AppLayout />,
                children: [
                  { path: "/onaylarim", element: <ConsentsPage /> },
                  { path: "/hesap-ve-guvenlik", element: <AccountSecurityPage /> },
                  { path: "/tercihler", element: <PreferencesPage /> },
                  /**
                   * Davetlerim merkezi (CLOS-227 `[146]`) — HESAP düzeyinde ve bu zorunlu:
                   * davetler farklı organizasyonlardan gelir, kullanıcı henüz hiçbirinin
                   * üyesi olmayabilir.
                   */
                  { path: "/davetlerim", element: <MyInvitationsPage /> },
                ],
              },

              /**
               * Haricî katılımcı görünümü (CLOS-147 `[128]`) — ÜYELİKSİZ kullanıcı için:
               * RequireOnboarding/RequireTenant DIŞINDA (o kapılar üyelik varsayar) ama
               * onay kapısının İÇİNDE (haricî kullanıcı da hukuki onay verir). Kabuk
               * AppLayout DEĞİL: iç sidebar ve çalışma alanı menüsü buraya sızamaz.
               */
              {
                path: "/harici",
                element: <ExternalShell />,
                children: [
                  { index: true, element: <ExternalContractsPage /> },
                  { path: "sozlesmeler/:id", element: <ExternalContractDetailPage /> },
                ],
              },
              {
                element: <RequireOnboarding />,
                children: [
                  { path: "/tenants", element: <TenantsPage /> },
                  {
                    element: <RequireTenant />,
                    children: [
                      {
                        path: "/app",
                        element: <AppLayout />,
                        children: [
                          { index: true, element: <HomePage /> },
                          /**
                           * Bekleyen İşlemler tam listesi (CLOS-181 `[163]`).
                           *
                           * ⚠ Kabuk İÇİNDE: liste bütün çalışma alanlarını kapsıyor ama
                           * kullanıcı ona uygulamanın içinden gidiyor ve geri
                           * dönebiliyor. Kabuk dışında bıraksaydık, sayfaya giren
                           * kullanıcı menüsüz kalırdı (elle turda görüldü).
                           */
                          { path: "bekleyen-islemler", element: <PendingTasksPage /> },
                          /**
                           * Bildirim Merkezi tam listesi (CLOS-186 `[168]`).
                           *
                           * ⚠ Kabuk İÇİNDE ve Bekleyen İşlemler'in KARDEŞİ: ikisi de Üst
                           * Bar panelinin "tamamını gör" hedefi. Kabuk dışında bıraksaydık
                           * sayfaya giren kullanıcı menüsüz kalırdı (`[163]`'ün elle
                           * turunda görülmüş kusur).
                           *
                           * ⚠ Adres AKTİF ALANA bağlı değil: bildirimler alan kapsamlı ama
                           * alan seçimi Üst Bar'da yaşıyor. Adrese alan kimliği koysaydık
                           * aynı sayfanın iki gerçek kaynağı olurdu.
                           */
                          { path: "bildirimler", element: <NotificationsPage /> },
                          /**
                           * D02'nin iki alt sayfası ORTAK KABUKTA (CLOS-625): kanonik
                           * bilgi mimarisinde Şablonlar, Sözleşmeler'in kardeşi değil alt
                           * sayfası. Kabuk yolsuz (pathless) bir rota; adresleri
                           * değiştirmeden yalnız bölüm gezinmesini ekliyor.
                           *
                           * Detay rotaları BİLEREK dışarıda: gezinme çubuğu bölüm
                           * sayfalarına ait, tek bir sözleşmenin detayına değil.
                           */
                          {
                            element: <ContractsLayout />,
                            children: [
                              /** Tüm Sözleşmeler (CLOS-115 `[083]`) — EP06'nın ilk ekranı. */
                              { path: "sozlesmeler", element: <ContractsPage /> },
                              /**
                               * ESKİ "Gelen Davetler" (CLOS-874) → Davetlerim (CLOS-1128 · Halit K2).
                               *
                               * ⚠ İkinci bir gelen-davet merkezi YOK: sözleşme tarafı daveti
                               * artık hesap seviyesindeki Davetlerim'de "hangi organizasyon
                               * adına?" seçiciyle yanıtlanıyor (CLOS-1133). Rota eski derin
                               * bağlantılar boşa düşmesin diye duruyor ve kanonik merkeze
                               * yönlendiriyor; kendi ekranı yok.
                               */
                              {
                                path: "sozlesmeler/gelen-davetler",
                                element: <Navigate to="/davetlerim" replace />,
                              },
                              /**
                               * Paylaşılan sözleşmeler (CLOS-873).
                               *
                               * ⚠ Kabuğun İÇİNDE ama "Tüm Sözleşmeler"in bir süzgeci
                               * DEĞİL: kayıt sahibin tenant'ında yaşıyor ve ayrı bir
                               * projeksiyondan okunuyor (`lib/contractSections.ts`).
                               */
                              {
                                path: "sozlesmeler/paylasilanlar",
                                element: <SharedContractsPage />,
                              },
                              /** Şablon kütüphanesi (CLOS-125 `[107]`). */
                              { path: "sablonlar", element: <TemplatesPage /> },
                            ],
                          },
                          /**
                           * Paylaşılan sözleşme görünümü (CLOS-873).
                           *
                           * ⚠ KABUĞUN DIŞINDA — `ContractDetailLayout` ile aynı karar:
                           * gezinme çubuğu bölüm sayfalarına ait, tek bir kaydın
                           * görünümüne değil.
                           */
                          {
                            path: "sozlesmeler/paylasilanlar/:contractId",
                            element: <SharedContractDetailPage />,
                          },
                          /**
                           * Sözleşme detayı (CLOS-117 `[085]`).
                           *
                           * Ayrı bir ROTA, listenin içinde açılan bir panel değil: kart
                           * *"liste-detay geçişi"*nden söz ediyor ve kullanıcı bir
                           * sözleşmeyi bağlantı olarak paylaşabilmeli. Kayıt erişimi
                           * bağlantıyı açanda YENİDEN doğrulanıyor (`[079]`).
                           */
                          /**
                           * ⚠ DETAY BİR KABUK, tek bir sayfa DEĞİL.
                           *
                           * Bölümler (Özet · Belge · Süreç · Taraflar ve Erişim · Kayıt)
                           * `ContractDetailLayout`'un ALT ROTALARI. Eskiden hepsi tek
                           * bileşendeydi ve `useState` ile seçilen sekme `hidden` sınıfıyla
                           * gizleniyordu: bölüme bağlantı verilemiyor, geri tuşu sekmeyi
                           * geri almıyor, yenilemede kullanıcı ilk sekmeye düşüyordu — ve
                           * beş bölümün bütün sorguları her açılışta koşuyordu.
                           *
                           * Kardeş kabuklar (`ContractsLayout`, `OrganizationLayout`) bu
                           * kuralı zaten uyguluyordu; burada yeni bir karar alınmıyor,
                           * var olan karar detaya da taşınıyor. Bölüm kaydı
                           * `lib/contractDetailSections.ts`.
                           */
                          {
                            path: "sozlesmeler/:id",
                            element: <ContractDetailLayout />,
                            children: [
                              { index: true, element: <ContractOverviewPage /> },
                              { path: "belge", element: <ContractDocumentHubPage /> },
                              { path: "surec", element: <ContractProcessPage /> },
                              { path: "taraflar", element: <ContractPartiesAccessPage /> },
                              { path: "kayit", element: <ContractRecordPage /> },
                            ],
                          },
                          /**
                           * Sözleşme metni editörü (CLOS-118 `[086]`).
                           *
                           * Detayın içinde bir bölüm değil AYRI rota: metin ekranın
                           * tamamını istiyor ve kullanıcı doğrudan editöre bağlantı
                           * verebilmeli. Kapılar sunucuda ve editör açık kalsa bile
                           * kaydetme kendi kararını yeniden veriyor.
                           */
                          /**
                           * ⚠ `/metin` ARTIK YÖNLENDİRME.
                           *
                           * Metin, "Belge" bölümünün KENDİSİ oldu: bir CLM ürününde
                           * sözleşmenin gövdesine ulaşmak için ekran değiştirmek yanlıştı.
                           * Adres silinmedi çünkü dışarıda yaşıyor — e-postalar, kayıtlı
                           * bağlantılar ve E2E senaryoları doğrudan buraya gidiyor.
                           * `replace`: geri tuşu kullanıcıyı yönlendirmeye geri sokmasın.
                           */
                          {
                            path: "sozlesmeler/:id/metin",
                            element: <MetinYonlendirmesi />,
                          },
                          /**
                           * Versiyon geçmişi ve versiyon detayı (CLOS-135 `[090]`).
                           *
                           * Detay AYRI rota: kart *"seçilen versiyon ayrı detay
                           * görünümünde açılır; DOĞRUDAN URL erişimi aynı permission/kayıt
                           * erişimi kontrollerinden geçer"* diyor.
                           */
                          {
                            path: "sozlesmeler/:id/versiyonlar",
                            element: <ContractVersionsPage />,
                          },
                          {
                            path: "sozlesmeler/:id/versiyonlar/:versionId",
                            element: <ContractVersionDetailPage />,
                          },
                          /**
                           * Belge görüntüleme ve indirme (CLOS-120 `[101]`). Versiyon
                           * detayının ALTINDA ayrı rota: belge, versiyonun bir yüzü —
                           * kendi başına paylaşılabilir bir adres, aynı kapılardan geçiyor.
                           */
                          {
                            path: "sozlesmeler/:id/versiyonlar/:versionId/belge",
                            element: <ContractDocumentPage />,
                          },
                          /**
                           * DOCX editörü (CLOS-1252 — ADR-0070). `/belge` ön izleme, bu
                           * DÜZENLEME yüzeyi: ikisi ayrı rota çünkü biri okuma biri yazma
                           * ve editör çekirdeği ~3,3 MB — ön izlemeye girene o yükü
                           * bindirmemek için ayrı tembel parça.
                           */
                          {
                            path: "sozlesmeler/:id/versiyonlar/:versionId/editor",
                            element: <ContractDocxEditor />,
                          },
                          /**
                           * Redline karşılaştırma (CLOS-141 `[096]`). Versiyonlar QUERY'de:
                           * `?left=&right=`. Yola koysaydık `/:versionId` detay rotasıyla
                           * çakışırdı — API tarafında da aynı sebeple query kullanılıyor.
                           */
                          /**
                           * Revizyon önerileri (CLOS-138 `[093]`, CLOS-139 `[094]`).
                           * Sözleşmenin altında ayrı adres: öneriler kaydın bir yüzü ve
                           * paylaşılabilir bir bağlantı, aynı kapılardan geçiyor.
                           */
                          {
                            path: "sozlesmeler/:id/revizyonlar",
                            element: <ContractRevisionsPage />,
                          },
                          {
                            path: "sozlesmeler/:id/karsilastir",
                            element: <ContractComparePage />,
                          },
                          /**
                           * Şablon kütüphanesi (CLOS-124 `[106]`). Liste ve detay `[107]`
                           * ile geliyor; oluşturma ekranı burada doğdu.
                           */
                          /**
                           * Şablondan sözleşme akışı (CLOS-131 `[114]`): önce metin
                           * onaya sunuluyor, sözleşme ondan sonra doğuyor.
                           */
                          {
                            path: "sozlesmeler/yeni/sablondan/:id",
                            element: <TemplateContractPage />,
                          },
                          /**
                           * AI ile taslak (CLOS-239 `[187]` · CLOS-995): şablon akışıyla
                           * AYNI yerde — ikisi de "sözleşme henüz YOK" adımı. Sözleşme
                           * ancak kullanıcı taslağı açıkça kabul edince doğuyor.
                           */
                          {
                            path: "sozlesmeler/yeni/ai-taslak",
                            element: <ContractAiDraftPage />,
                          },
                          {
                            path: "sablonlar/yeni",
                            element: <TemplateCreatePage />,
                          },
                          /**
                           * Detay AYRI rota (CLOS-125 `[107]`): kullanıcı bir şablonu
                           * meslektaşına bağlantı olarak gönderebilmeli.
                           */
                          { path: "sablonlar/:id", element: <TemplateDetailPage /> },
                          { path: "sablonlar/:id/duzenle", element: <TemplateEditPage /> },
                          /**
                           * Müvekkiller (CLOS-169 `[116]`) — EP11'in ilk ekranı.
                           *
                           * Detay rotası BİLEREK yok: müvekkil detayı `[117]`'nin
                           * (CLOS-170) işi ve kendi kayıt erişimi kapısını kuracak.
                           * Şimdiden açsaydık kartsız bir yüzey doğardı (ADR-0014).
                           */
                          /**
                           * Müvekkil Alanı yüzeyi (CLOS-629 `[123]`) — müvekkilin KENDİ alanı.
                           * Erişim kapısı sunucuda: alan `client_workspace` değilse uçlar 403
                           * dönüyor ve sayfa açık ekranı güvenli biçimde kapatıyor.
                           */
                          { path: "muvekkil-alani", element: <ClientWorkspacePage /> },
                          /**
                           * ⚠ Müvekkil rotaları `client.view` KAPISININ ardında (CLOS-814).
                           * Yetenek türe ve kişisel alanda arketipe bağlı; tür düzeltmesi
                           * ya da alan değişimi sonrasında açık kalmış bir sekme burada
                           * kapanıyor.
                           */
                          {
                            element: <RequirePermission permission="client.view" />,
                            children: [
                              /**
                               * D03 kabuğu (CLOS-875): liste ve gelen talepler ORTAK
                               * sekmelerde. Detay rotası BİLEREK dışarıda — gezinme çubuğu
                               * bölüm sayfalarına ait (`ContractsLayout` ile aynı karar).
                               */
                              {
                                element: <ClientsLayout />,
                                children: [
                                  { path: "muvekkiller", element: <ClientsPage /> },
                                  {
                                    /*
                                      ⚠ ROTA `muvekkiller/:id`'DEN ÖNCE tanımlı olmalı ve
                                      burada öyle: aşağıdaki detay rotası sonra geliyor.
                                      Sonra tanımlansaydı "sponsorlu-alanlar" bir müvekkil
                                      kimliği sanılırdı — sunucu tarafında aynı tuzağa
                                      düşülmüştü ve orada da bekçisi yazıldı.
                                    */
                                    path: "muvekkiller/sponsorlu-alanlar",
                                    element: <SponsoredWorkspacesPage />,
                                  },
                                ],
                              },
                              /**
                               * Müvekkil detayı (CLOS-170 `[117]`) — AYRI rota, listenin
                               * içinde panel değil: bir hukukçu müvekkil kaydını meslektaşına
                               * bağlantı olarak gönderebilmeli ve doğrudan URL de aynı
                               * sunucu kararından geçiyor.
                               */
                              { path: "muvekkiller/:id", element: <ClientDetailPage /> },
                            ],
                          },
                          /**
                           * Organizasyon Yönetimi (EP13). `[050]` profili, `[051]`
                           * kullanıcıları açtı; rol sayfası `[053]` ile aynı dalın
                           * altına gelecek.
                           *
                           * Bölümler AYRI ROTALAR (sekme durumu değil): kart doğrudan
                           * URL yüzeylerinden söz ediyor ve yönetici kullanıcı listesini
                           * bir bağlantı olarak paylaşabilmeli.
                           */
                          /**
                           * Abonelik ve Kullanım (D06 — CLOS-197 `[173]`).
                           *
                           * ⚠ Kabuk İÇİNDE ve kendi sekme kaydıyla: kart *"D06 tek sidebar
                           * sayfasıdır"* diyor. CLOS-202/201 ile Faturalama ve Ek Haklar
                           * sekmeleri eklendi; kullanım sayaçlarının (CLOS-200) kaynağı hâlâ
                           * yok ve o bölüm hâlâ çizilmiyor (`subscriptionSections.ts`).
                           *
                           * ⚠ Rota YETKİYLE GİZLENMİYOR: yetkisi olmayan kullanıcı sayfayı
                           * açıp sunucunun 403'ünü SEBEBİYLE görüyor. Gizleseydik sessiz bir
                           * 404 alır ve niye giremediğini öğrenemezdi (193'ün kararı).
                           */
                          {
                            path: "abonelik",
                            element: <SubscriptionLayout />,
                            children: [
                              { index: true, element: <SubscriptionOverviewPage /> },
                              { path: "ek-haklar", element: <AddOnsPage /> },
                              { path: "faturalama", element: <BillingPage /> },
                            ],
                          },
                          {
                            path: "organizasyon",
                            element: <OrganizationLayout />,
                            children: [
                              { index: true, element: <OrganizationProfilePage /> },
                              { path: "kullanicilar", element: <MembersPage /> },
                              { path: "departmanlar", element: <DepartmentsPage /> },
                              { path: "guvenlik", element: <SecurityPolicyPage /> },
                              { path: "uyarilar", element: <AlertPolicyPage /> },
                              { path: "roller", element: <RolesPage /> },
                              /** İş Akışları — Roller ve Yetkiler'in hemen ardında (UI/UX §4). */
                              { path: "is-akislari", element: <WorkflowsPage /> },
                              {
                                path: "is-akislari/:id",
                                element: <WorkflowBuilderPage />,
                              },
                              {
                                path: "katilimci-rolleri",
                                element: <ParticipantRolesPage />,
                              },
                              /**
                               * Audit Kayıtları (CLOS-193 `[171]`).
                               *
                               * ⚠ ADRES DEĞİŞTİ: `/app/audit` → `/app/organizasyon/audit`.
                               *
                               * İlk yazımda ekran kabuğun doğrudan çocuğuydu ve hiçbir
                               * yerden BAĞLANTI ALMIYORDU — yalnız adres çubuğuna elle
                               * yazan ulaşabiliyordu. Canlı turda görüldü. Kartın kanonik
                               * referansı D05-P05 "Organizasyon Yönetimi" ve defter tam
                               * olarak oraya ait: organizasyon geneli bir kayıt.
                               *
                               * ⚠ Rota YETKİYLE GİZLENMİYOR. Yetkisi olmayan kullanıcı
                               * sayfayı açabiliyor ve sunucunun 403'ünü SEBEBİYLE görüyor;
                               * rotayı kaldırsaydık sessiz bir 404 alır ve niye
                               * giremediğini öğrenemezdi. Görünürlük kararı yalnız
                               * SEKMEDE (`organizationSections.ts`).
                               */
                              { path: "audit", element: <AuditPage /> },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: "/", element: <Navigate to="/app" replace /> },
  { path: "*", element: <Navigate to="/" replace /> },
]);
